import {
  Button,
  Checkbox,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import {PolicyDetailDto} from 'Agent/Policies/api';
import policyApi from 'Agent/Policies/api/policyApi';
import {fetchPolicy} from 'Agent/Policies/ducks/policy';
import {PolicyDocumentType, PolicyDocumentTypes} from 'Agent/Policies/models/PolicyDocumentTypes';
import {PolicySource} from 'Agent/Policies/models/PolicySource';
import {TheFraudWarning} from 'Agent/Policies/models/TheFraudWarning';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import classnames from 'classnames';
import {createSnackbar} from 'Core/ducks/snackbar';
import {matchAsync} from 'Core/models/Async';
import React, {MouseEvent, useEffect, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useParams} from 'react-router';
import {ReducerState} from 'rootReducer';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import Modal from 'Shared/components/Modal';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import './PolicyUploadDocument.sass';
import {pipe} from 'fp-ts/lib/pipeable';
import * as E from 'fp-ts/lib/Either';

type PolicyUploadDocumentProps = WithSelectedAgencyProps;

const PolicyUploadDocument = (props: PolicyUploadDocumentProps) => {
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));
  const {selectedAgency} = props;
  const {policyId, policyNumber} = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const {policy} = useSelector((state: ReducerState) => state.policy);

  useEffect(() => {
    if (policyId && policyNumber) {
      dispatch(fetchPolicy(selectedAgency.encryptedIdentifier, policyId, policyNumber));
    }
  }, [policyId, policyNumber]);

  type FileUploadErrors = {
    selectedFormError: string | boolean;
    fileError: string | boolean;
  };

  const [policyUploadDocumentId, setPolicyUploadDocumentId] = useState('');
  const [file, setFile] = useState<File>();
  const [agreeToTerm, setAgreeToTerm] = useState(false);
  const [fraudWarningDialog, setFraudWarningDialog] = useState(false);
  const [errors, setErrors] = useState<FileUploadErrors>({selectedFormError: false, fileError: false});

  const MAX_SIZE = 10485760; // 10 MB in bytes.
  const onDropAccepted = (acceptedFiles: File[]) => {
    setFile(acceptedFiles[0]);
    setErrors({
      ...errors,
      fileError: false,
    });
  };
  const onDropRejected = (acceptedFiles: File[]) => {
    setFile(undefined);
    setErrors({
      ...errors,
      fileError: acceptedFiles[0].size > MAX_SIZE ? 'File Exceeds Maximum Allowed Size.' : 'File Format Not Accepted.',
    });
  };

  const {getRootProps, getInputProps} = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxSize: MAX_SIZE,
    accept: 'image/jpg, image/jpeg, image/tif, image/tiff, application/pdf',
  });

  const viewFraudWarningDialog = (event: MouseEvent) => {
    event.preventDefault();

    setFraudWarningDialog(true);
  };

  const uploadFile = () => {
    if (policyUploadDocumentId && file) {
      policyApi
        .uploadPolicyDocument(
          selectedAgency.encryptedIdentifier,
          file,
          policyUploadDocumentId,
          policyId!,
          policyNumber!
        )
        .then(result =>
          pipe(
            result,
            E.fold(
              () => {
                dispatch(createSnackbar('Document upload failed.'));
              },
              () => {
                dispatch(createSnackbar('Document successfully uploaded.'));
              }
            )
          )
        );

      history.push(PolicyRouteConstants.POLICY_DETAILS(policyId!, policyNumber!));
    } else {
      setErrors({
        selectedFormError: !policyUploadDocumentId && 'Valid form required.',
        fileError: !file && 'Valid file required.',
      });
    }
  };

  const handlePolicyDocumentTypeChange = (e: React.ChangeEvent<{name?: string; value: unknown}>) => {
    setPolicyUploadDocumentId(e.target.value as string);
    setErrors({
      ...errors,
      selectedFormError: false,
    });
  };

  const renderDropzone = () => (
    <section className="policy-upload-document__file-container">
      <div {...getRootProps({className: 'policy-upload-document__dropzone'})}>
        <input {...getInputProps()} type="file" multiple={false} hidden />
        <Typography variant="body2" color={!!errors.fileError ? 'error' : 'initial'}>
          {(file && file.name) || errors.fileError || 'Drop File Here Or Click To Browse'}
        </Typography>
      </div>
    </section>
  );

  return matchAsync(policy, {
    Conflict: error => <ErrorPage error={error} />,
    Loaded: (result: PolicyDetailDto) => {
      const lifePolicyFilter = (type: PolicyDocumentType) =>
        !result.lifePlans || result.lifePlans.length === 0 || type.label !== 'Claim Document';
      const newYorkPolicyFilter = (type: PolicyDocumentType) =>
        result.source!.id === PolicySource.NewYork
          ? type.label !== 'HIPAA Authorization'
          : type.label !== 'HIPAA Authorization - New York';

      return (
        <>
          <Grid
            container
            spacing={3}
            className={classnames('policy-upload-document', {
              'policy-upload-document--desktop': isDesktop,
            })}
          >
            <Grid item container>
              <Typography variant="h6">Upload A Document</Typography>
            </Grid>
            <Grid item container spacing={3}>
              <Grid item>
                <Typography variant="subtitle2">Policy Number: {result.policyNumber}</Typography>
              </Grid>
              {result.insured && (
                <Grid item>
                  <Typography variant="subtitle2">Insured: {result.insured.fullName}</Typography>
                </Grid>
              )}
            </Grid>
            <Paper className="policy-upload-document__container">
              <Grid item container spacing={3} direction="column">
                <Grid item>
                  <Typography variant="subtitle1">Uploads must be in the TIF, PDF, or JPG formats.</Typography>
                </Grid>
                <Grid item container spacing={3} justify="center">
                  {renderDropzone()}
                </Grid>
                <Grid item>
                  <FormControl
                    className="policy-upload-document__document-type-selector"
                    error={!!errors.selectedFormError}
                  >
                    <InputLabel id="formSelectorLabel">Select A Form</InputLabel>
                    <Select
                      id="document-type-Selector"
                      value={policyUploadDocumentId}
                      onChange={handlePolicyDocumentTypeChange}
                    >
                      {PolicyDocumentTypes.filter(lifePolicyFilter)
                        .filter(newYorkPolicyFilter)
                        .map((type, index) => (
                          <MenuItem key={index} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>{errors.selectedFormError}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item container direction="column">
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={agreeToTerm}
                        onChange={() => setAgreeToTerm(!agreeToTerm)}
                        required
                      />
                    }
                    label="By Clicking this box and selecting SUBMIT, I hereby confirm that:"
                  />
                  <Grid item>
                    <ol>
                      <li>
                        The information I am submitting is for this policy/certificate and at the
                        policy/certificateholder's request.
                      </li>
                      <li>
                        The information I am submitting is true and complete to the best of my knowledge and belief.
                      </li>
                      <li>
                        I have read{' '}
                        <Link href="" onClick={viewFraudWarningDialog}>
                          the fraud warning
                        </Link>
                        for my state.
                      </li>
                    </ol>
                  </Grid>
                </Grid>
                <Grid item>
                  <Button
                    color="primary"
                    onClick={uploadFile}
                    disabled={!agreeToTerm || !policyUploadDocumentId || !file}
                  >
                    Submit
                  </Button>
                  <Button
                    color="primary"
                    onClick={() => history.push(PolicyRouteConstants.POLICY_DETAILS(policyId!, policyNumber!))}
                  >
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Modal title="Fraud Warning" open={fraudWarningDialog} onClose={() => setFraudWarningDialog(false)}>
            <DialogContent>
              <Typography className="policy-upload-document__fraud-warning" variant="body1">
                {TheFraudWarning.join('\n\n')}
              </Typography>
            </DialogContent>
          </Modal>
        </>
      );
    },
    Loading: () => <Loading />,
    ServerError: error => <ErrorPage error={error} />,
    Unauthorized: error => <ErrorPage error={error} />,
  });
};

export default withSelectedAgency(PolicyUploadDocument);
